// 定义事件处理函数类型
type EventHandler<T = unknown> = (data: T) => void;

// 定义事件总线接口
interface EventBus {
    on<T = unknown>(event: string, handler: EventHandler<T>): void;
    off<T = unknown>(event: string, handler?: EventHandler<T>): void;
    emit<T = unknown>(event: string, data?: T): void;
    once<T = unknown>(event: string, handler: EventHandler<T>): void;
}

// 实现事件总线
class EventBusImpl implements EventBus {
    private events: Record<string, EventHandler[]> = {};

    on<T = unknown>(event: string, handler: EventHandler<T>): void {
        if (!this.events[event]) {
            this.events[event] = [];
        }
        this.events[event].push(handler as EventHandler);
    }

    off<T = unknown>(event: string, handler?: EventHandler<T>): void {
        if (!this.events[event]) return;

        if (!handler) {
            // 移除该事件的所有监听器
            delete this.events[event];
            return;
        }

        // 移除特定的处理函数
        this.events[event] = this.events[event].filter(
            h => h !== handler
        );

        // 如果没有监听器了，删除事件
        if (this.events[event].length === 0) {
            delete this.events[event];
        }
    }

    emit<T = unknown>(event: string, data?: T): void {
        if (!this.events[event]) return;

        // 调用所有处理函数
        this.events[event].forEach(handler => {
            try {
                handler(data);
            } catch (e) {
                console.error(`Error handling event ${event}:`, e);
            }
        });
    }

    once<T = unknown>(event: string, handler: EventHandler<T>): void {
        const onceHandler = (data: T) => {
            handler(data);
            this.off(event, onceHandler);
        };
        this.on(event, onceHandler);
    }
}

// 创建全局事件总线实例
const eventBus = new EventBusImpl();

// 导出事件总线实例
export default eventBus;

// 定义常用事件类型（可选）
export const AUTH_CHANGE_EVENT = 'authChange';
export const SESSION_EXPIRED_EVENT = 'sessionExpired';
export const API_ERROR_EVENT = 'apiError';
export const NETWORK_ERROR_EVENT = 'networkError';
